import { useEPGService } from '@/app/context/DIContext/DIServices'
import { EpgObject, ExtendedEpgElement } from '@/types/epg/Epg'
import { RawProgramObject } from '@/types/epg/Program'
import { epgChannelRequest } from '@/types/epg/epgChannelRequest'
import Program from '@/shared/classes/epg/program/program'
import { checkIfProgramCurrentlyRuns } from '@/shared/classes/epg/helpers/isProgramRunning'
import {
    getFormattedProgramDuration,
    getProgramDurationProgress,
} from '@/shared/classes/epg/helpers/programDurationHelpers'
import { filterProgramsByTime } from '@/shared/hooks/EPG/lib'
import {
    CACHE_TRIM_AMOUNT,
    MAX_CHANNELS_FETCH_AMOUNT,
    MILIS_24h,
    presentFollowingDuration,
    presentFollowingId,
    PROGRAMS_LENGTH_LIMIT,
} from '@/shared/constants'
import { IEPGServiceToya } from '@/shared/types/services/types/epgService/epgServiceInterfaces'
import { getPrograms } from '@/shared/classes/epg/helpers/getPrograms'
import { getErrorMessage } from '@/shared/lib'
import { ChannelType } from '@/shared/types/appTypes'
import { postWithAuth } from '@/api/httpRequests'
import { epgEndpoint } from '@/api/toya/apiConstantsCodespot'

const useEPGToyaRepository = () => {
    const {
        createExtendedEpg,
        filterWidevineOttEpg,
        sortEpgElements,
        rawProgramsToFullPrograms,
        getProgramsForAllChannels,
    } = useEPGService() as IEPGServiceToya

    const filterEpgList = (
        epg: ExtendedEpgElement[],
        channels: ChannelType[]
    ) => filterWidevineOttEpg(epg, channels)

    const sortEpgList = (array: ExtendedEpgElement[]) => sortEpgElements(array)
    const createFullProgramObject = (array: RawProgramObject[]) =>
        rawProgramsToFullPrograms(array)

    const getMissingPrograms = async (
        list?: epgChannelRequest[],
        isPresentFollowing?: boolean
    ) => {
        if (!list && !isPresentFollowing) return
        const defaultStartEpochSecs = Math.ceil(Date.now() / 1000) // Date.now() / 1000 i zaokrąglić
        const stringifiedChannels = isPresentFollowing
            ? JSON.stringify({
                  channelIds: presentFollowingId,
                  date: defaultStartEpochSecs,
                  duration: presentFollowingDuration,
              })
            : JSON.stringify(list)

        const programsResponse = await postWithAuth(
            epgEndpoint,
            true,
            stringifiedChannels
        )
        return programsResponse
    }

    const fetchEpgData = async (
        channelsParams?: epgChannelRequest[],
        isPresentFollowing?: boolean
    ): Promise<EpgObject[]> => {
        if (!channelsParams && !isPresentFollowing) return []
        const response = await getMissingPrograms(
            channelsParams,
            isPresentFollowing
        )
        if (Array.isArray(response) && response.length) {
            return response.map(({ channelId, programs }) => ({
                channelId,
                programs: createFullProgramObject(programs),
            }))
        }
        return []
    }

    const getUpdatedPrograms = (
        programs: Program[],
        newPrograms: Program[],
        direction: string | null,
        getCurrentProgramFunc: (programs: Program[]) => {
            currentProgram: Program | undefined
            programDurationProgress: number
            programFormattedDuration: string
        },
        milis24h: number,
        filterProgramsByTime: (
            programs: Program[],
            startTimeBoundary: number
        ) => Program[]
    ) => {
        switch (direction) {
            case 'up':
                return [...newPrograms, ...programs]
            case 'down':
                return [...programs, ...newPrograms]
            case 'firstRender': {
                const { currentProgram } = getCurrentProgramFunc([
                    ...programs,
                    ...newPrograms,
                ])
                return currentProgram
                    ? filterProgramsByTime(
                          newPrograms,
                          currentProgram.startMilis - milis24h
                      )
                    : newPrograms
            }
            default:
                return programs
        }
    }

    const trimPrograms = (
        programs: Program[],
        direction: string | null,
        trimAmount: number
    ) => {
        if (direction === 'up') {
            const endTrimPoint = trimAmount
            return programs.slice(0, programs.length - endTrimPoint)
        } else {
            const startTrimPoint = trimAmount
            return programs.slice(startTrimPoint)
        }
    }

    const getCurrentProgram = (fullyNamesPrograms: Program[]) => {
        let currentProgram: Program | undefined
        let programDurationProgress = 0
        let programFormattedDuration = ''

        if (fullyNamesPrograms.length !== 0) {
            fullyNamesPrograms.forEach((fullyNamedProgram: Program) => {
                const { startMilis, endMilis, durationMilis } =
                    fullyNamedProgram
                if (checkIfProgramCurrentlyRuns(startMilis, endMilis) === 0) {
                    currentProgram = fullyNamedProgram
                    programDurationProgress = Math.floor(
                        getProgramDurationProgress(startMilis, durationMilis)
                    )
                    programFormattedDuration = getFormattedProgramDuration(
                        startMilis,
                        endMilis
                    )
                }
            })
        }
        return {
            currentProgram,
            programDurationProgress,
            programFormattedDuration,
        }
    }

    const updateEpgDataWithNewPrograms = (
        epgData: ExtendedEpgElement[],
        convertedResponse: {
            channelId: string
            programs: Program[]
        }[],
        direction: string | null
    ): ExtendedEpgElement[] =>
        epgData.map((epgChannel) => {
            const update = convertedResponse.find(
                (item) => item.channelId === epgChannel.channelId
            )

            if (!update) return epgChannel

            let updatedPrograms = getUpdatedPrograms(
                epgChannel.programs,
                update.programs,
                direction,
                getCurrentProgram,
                MILIS_24h,
                filterProgramsByTime
            )

            if (updatedPrograms.length > PROGRAMS_LENGTH_LIMIT) {
                updatedPrograms = trimPrograms(
                    updatedPrograms,
                    direction,
                    CACHE_TRIM_AMOUNT
                ).sort(
                    (programA, programB) =>
                        programA.startMilis - programB.startMilis
                )
            }

            return {
                ...epgChannel,
                programs: updatedPrograms,
            }
        })

    const mergeEpgArrays = (
        arr1: ExtendedEpgElement[],
        arr2: ExtendedEpgElement[]
    ): ExtendedEpgElement[] => {
        const mergedMap = new Map<string, ExtendedEpgElement>()

        const addOrMerge = (element: ExtendedEpgElement) => {
            const existingElement = mergedMap.get(element.channelId)

            if (existingElement) {
                const uniquePrograms = new Map<string, Program>()

                existingElement.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                existingElement.programs = Array.from(uniquePrograms.values())
            } else {
                const uniquePrograms = new Map<string, Program>()
                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )
                element.programs = Array.from(uniquePrograms.values())

                mergedMap.set(element.channelId, element)
            }
        }

        arr1.forEach(addOrMerge)
        arr2.forEach(addOrMerge)

        return Array.from(mergedMap.values())
    }

    const prepareEPGforSetToState = async (
        channels: ChannelType[],
        createExtendedEpg: IEPGServiceToya['createExtendedEpg'],
        filterWidevineOttEpg: IEPGServiceToya['filterWidevineOttEpg'],
        sortEpgElements: IEPGServiceToya['sortEpgElements']
    ) => {
        try {
            const channelsId = channels.map((channel) => channel.id || '')
            const apiEpg = await getPrograms(channelsId)
            const rawRemoteEpg = createExtendedEpg(channels, apiEpg)
            if (rawRemoteEpg) {
                const widevineOttEpg = filterWidevineOttEpg(
                    rawRemoteEpg,
                    channels
                )
                const sortedEpg = sortEpgElements(widevineOttEpg)
                return sortedEpg
            }
        } catch (error) {
            getErrorMessage('setEpg', error)
            throw error
        }
    }
    const createEPGList = async (channelsList: ChannelType[]) => {
        const leftDirectionArray = channelsList.slice(
            -MAX_CHANNELS_FETCH_AMOUNT
        )
        const rightDirectionArray = channelsList.slice(
            0,
            MAX_CHANNELS_FETCH_AMOUNT
        )
        const epgListEnd = await prepareEPGforSetToState(
            leftDirectionArray,
            createExtendedEpg,
            filterEpgList,
            sortEpgList
        )
        const epgListBeginning = await prepareEPGforSetToState(
            rightDirectionArray,
            createExtendedEpg,
            filterEpgList,
            sortEpgList
        )
        let epgList: ExtendedEpgElement[] = []
        if (epgListEnd && epgListBeginning) {
            epgList = [...epgListEnd, ...epgListBeginning]
        }
        return epgList
    }
    const createInitialExtendedEpg = (channels: ChannelType[]) => {
        const initialEmptyProgramsEpg: ExtendedEpgElement[] = []
        channels.forEach((channel: ChannelType) => {
            const { id, lcn, name, icon } = channel
            const fullObject = {
                channelIcon: icon || '',
                channelName: name || '',
                channelNumber: parseInt(lcn || ''),
                channelId: id ? id.toString() : '',
                programs: [],
            }
            initialEmptyProgramsEpg.push(fullObject)
        })
        return initialEmptyProgramsEpg
    }

    const createEPG = async (
        channelsList: ChannelType[],
        programs: EpgObject[]
    ): Promise<ExtendedEpgElement[]> => {
        const tvPresentFollowing = createExtendedEpg(channelsList, programs)
        const initialEpg = createInitialExtendedEpg(channelsList)
        const epgList = await createEPGList(channelsList)
        if (epgList.length && tvPresentFollowing) {
            const epg = mergeEpgArrays(epgList, tvPresentFollowing)
            const fullEpg = mergeEpgArrays(epg, initialEpg).sort(
                (itemA, itemB) => itemA.channelNumber - itemB.channelNumber
            )

            return fullEpg
        }
        return []
    }

    return {
        mergeEpgArrays,
        filterEpgList,
        sortEpgList,
        createFullProgramObject,
        fetchEpgData,
        getUpdatedPrograms,
        trimPrograms,
        getCurrentProgram,
        updateEpgDataWithNewPrograms,
        prepareEPGforSetToState,
        getProgramsForAllChannels,
        createEPG,
        createExtendedEpg,
    }
}

export { useEPGToyaRepository }
